import React from 'react';
import PropTypes from 'prop-types';
import TableWindow from '../../components/Table/TableWindow';
import useTablePages from '../../hooks/useTablePages';
import activeColumns, { archivedColumns } from './Notices.columns';
import { Flex } from 'rebass';
import { useOrderBy } from '../../hooks/useOrderBy';
import PageBar from '../../components/PageBar/PageBar';
import { RouteComponentProps, withRouter } from 'react-router';
import useThirdPaneCallbacks from '../../hooks/useThirdPaneCallbacks';
import useMode from '../../hooks/useMode';
import Layout from '../../components/Layout/Layout';
import useColumnConfiguration from '../../hooks/useColumnConfiguration';
import Button from '../../components/Button/Button';
import { useHistory } from 'react-router-dom';
import { GET_NOTICE_IDS, GET_NOTICES } from './Notices.queries';
import { NoticeItem } from './Notices.types';
import FilterBarNotice, {
  defaultFilters,
  NoticeFilters,
} from './FilterBarNotice';
import useStoredFilters from '../../hooks/useStoredFilters';
import RequirePerms from '@/components/RequirePerms';

type NoticesProps = RouteComponentProps<{
  id?: string;
}>;

export const convertFilters = (storedFilters: NoticeFilters) => {
  const query: { and: Record<string, any>[] } = { and: [] };
  if (storedFilters.archived) {
    query.and.push({
      archived: storedFilters.archived,
    });
  }

  if (query.and.length === 0) {
    delete (query as any).and;
  }

  return query;
};

/**
 * @component Used for customers page
 */
function Notices({ match }: NoticesProps): JSX.Element {
  const { storedFilters, setStoredFilters } = useStoredFilters<NoticeFilters>(
    (filters) => convertFilters(filters),
    'jobs',
    defaultFilters
  );

  const isArchived = storedFilters.archived === 'archived';

  const availableColumns = isArchived ? archivedColumns : activeColumns;

  const { orderBy, onHeaderClick } = useOrderBy(
    'notices',
    availableColumns,
    'id'
  );

  const { loading, error, itemIds, itemLoader } = useTablePages<NoticeItem>(
    'notices',
    GET_NOTICE_IDS,
    {
      where: {
        archived: isArchived ? { not: null } : null,
      },
      limit: null,
      orderBy,
    },
    GET_NOTICES,
    (ids) => ({
      where: {
        id: ids,
      },
      limit: null,
      orderBy,
    })
  );

  const history = useHistory();
  const mode = useMode();

  const { onRowClick } = useThirdPaneCallbacks(
    `/${useMode()}/notices`,
    itemIds
  );

  const { visibleColumns } = useColumnConfiguration(
    availableColumns,
    'notices'
  );

  return (
    <Layout>
      <Flex
        flexGrow={1}
        flexDirection="column"
        px="mainContent"
        pb="mainContent"
      >
        <PageBar title="Notices">
          <RequirePerms notices="CREATE">
            <Button
              ml={3}
              variant="primary"
              icon="plus"
              onClick={() => {
                history.push(`/${mode}/notices/add`);
              }}
            >
              Add Notice
            </Button>
          </RequirePerms>
        </PageBar>

        <FilterBarNotice
          filters={storedFilters}
          setFilters={setStoredFilters}
          selectedId={match.params.id}
        />

        {loading && itemIds.length === 0 ? (
          'Loading...'
        ) : (
          <TableWindow
            emptyMessage="No notices found."
            columns={visibleColumns}
            itemIds={itemIds}
            itemLoader={itemLoader}
            activeIds={match.params.id ? [match.params.id] : undefined}
            onHeaderClick={onHeaderClick}
            onRowClick={onRowClick}
            orderBy={orderBy}
            showActionBar={false}
          />
        )}
      </Flex>
    </Layout>
  );
}

Notices.propTypes = {
  match: PropTypes.any,
};

export default withRouter(Notices);
